import { useCallback, useEffect, useState, useRef } from "react";
import { api, API, idrShort } from "../lib/api";
import { Plus, Trash2, X, Upload, Download, FileText, ClipboardList, Calendar, Users, AlertCircle, CheckCircle2, Circle, Clock, Loader2, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { DatePicker } from "@/components/ui/date-picker";
import { hariIniISO, formatTanggal } from "@/lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";

const PRIORITY = [
  { v: "Low", tone: "bg-slate-100 text-slate-700 border-slate-200" },
  { v: "Medium", tone: "bg-sky-100 text-sky-800 border-sky-200" },
  { v: "High", tone: "bg-rose-100 text-rose-800 border-rose-200" },
];
const STATUS = [
  { v: "Open", icon: Circle, tone: "bg-slate-100 text-slate-700" },
  { v: "In-Progress", icon: Loader2, tone: "bg-amber-100 text-amber-800" },
  { v: "Review", icon: Clock, tone: "bg-sky-100 text-sky-800" },
  { v: "Done", icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-800" },
];

const emptyForm = {
  judul: "", deskripsi: "", employee_ids: [],
  priority: "Medium", status: "Open", deadline: "",
};

export default function Projects() {
  const { user } = useAuth();
  const canManage = user?.role === "superadmin" || user?.role === "admin";
  const isEmp = user?.role === "employee";

  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [filter, setFilter] = useState({ status: "", employee_id: "" });
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detailFor, setDetailFor] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (filter.status) p.set("status", filter.status);
    if (filter.employee_id) p.set("employee_id", filter.employee_id);
    api.get(`/projects?${p}`).then(r => setItems(r.data));
  }, [filter.status, filter.employee_id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isEmp) api.get("/employees").then(r => setEmps(r.data.filter(e => e.is_active !== false)));
  }, [isEmp]);

  const openNew = () => { setForm(emptyForm); setEditing(null); setPendingFiles([]); setModal(true); };
  const openEdit = (p) => {
    setForm({
      judul: p.judul, deskripsi: p.deskripsi || "",
      employee_ids: p.employee_ids || [], priority: p.priority || "Medium",
      status: p.status || "Open", deadline: p.deadline || "",
    });
    setEditing(p.id); setPendingFiles([]); setModal(true);
  };

  const toggleAssignee = (eid) => setForm(s => ({
    ...s,
    employee_ids: s.employee_ids.includes(eid)
      ? s.employee_ids.filter(x => x !== eid)
      : [...s.employee_ids, eid],
  }));

  const readAsBase64 = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(f);
  });

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const rows = [];
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: > 10MB (dilewati)`); continue; }
      const base64 = await readAsBase64(f);
      rows.push({ name: f.name, mime: f.type || "application/octet-stream", size: f.size, base64 });
    }
    setPendingFiles(s => [...s, ...rows]);
    e.target.value = "";
  };

  const removePending = (i) => setPendingFiles(s => s.filter((_, x) => x !== i));

  const save = async () => {
    if (!form.judul.trim()) { toast.error("Judul wajib diisi"); return; }
    if (!editing && form.employee_ids.length === 0) { toast.error("Pilih minimal 1 karyawan"); return; }
    try {
      let projectId = editing;
      if (editing) {
        await api.patch(`/projects/${editing}`, form);
      } else {
        const { data } = await api.post("/projects", { ...form, files: [] });
        projectId = data.id;
      }
      // upload pending files after create/update
      for (const f of pendingFiles) {
        await api.post(`/projects/${projectId}/files`, f);
      }
      toast.success(editing ? "Tugas diperbarui" : "Tugas dibuat");
      setModal(false); setPendingFiles([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Form tugas (judul, deskripsi, assignee multi-pilih, lampiran file yang
  // sudah dipilih tapi belum ter-upload) — JSX modal ada di komponen halaman
  // yang tidak unmount, jadi `active` mengikuti `modal`.
  const projectGuard = useModalGuard({
    active: modal, dangerous: true,
    onClose: () => setModal(false), onSubmit: save,
  });

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/projects/${id}`, { status });
      toast.success("Status diperbarui"); load();
      if (detailFor?.id === id) setDetailFor({ ...detailFor, status });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const del = async (id) => {
    if (!confirm("Hapus tugas ini?")) return;
    await api.delete(`/projects/${id}`); load(); setDetailFor(null);
  };

  const downloadFile = async (pid, fname) => {
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/projects/${pid}/files/${encodeURIComponent(fname)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Gagal download"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = fname;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const deleteFile = async (pid, fname) => {
    if (!confirm(`Hapus file ${fname}?`)) return;
    await api.delete(`/projects/${pid}/files/${encodeURIComponent(fname)}`);
    load();
    if (detailFor?.id === pid) {
      const upd = await api.get(`/projects/${pid}`);
      setDetailFor(upd.data);
    }
  };

  const refreshDetail = async (pid) => {
    const upd = await api.get(`/projects/${pid}`);
    setDetailFor(upd.data);
    load();
  };

  const addComment = async (text) => {
    if (!text.trim()) return false;
    try {
      await api.post(`/projects/${detailFor.id}/comments`, { text: text.trim() });
      await refreshDetail(detailFor.id);
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); return false; }
  };

  const deleteComment = async (cid) => {
    if (!confirm("Hapus komentar?")) return;
    try {
      await api.delete(`/projects/${detailFor.id}/comments/${cid}`);
      await refreshDetail(detailFor.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-start justify-between mb-6 md:mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Projects &amp; Tugas</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Penugasan Karyawan</h1>
          <p className="mt-2 text-sm text-slate-500">Kelola tugas &amp; project — assign ke karyawan, lampirkan file, pantau status.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={filter.status} onChange={e => setFilter(s => ({ ...s, status: e.target.value }))}
            data-testid="filter-status"
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="">Semua status</option>
            {STATUS.map(s => <option key={s.v}>{s.v}</option>)}
          </select>
          {!isEmp && (
            <select value={filter.employee_id} onChange={e => setFilter(s => ({ ...s, employee_id: e.target.value }))}
              data-testid="filter-employee"
              className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Semua karyawan</option>
              {emps.map(e => <option key={e.id} value={e.id}>{e.nama}</option>)}
            </select>
          )}
          {canManage && (
            <button onClick={openNew} data-testid="btn-add-project"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">
              <Plus className="w-4 h-4" /> Buat Tugas
            </button>
          )}
        </div>
      </header>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-display font-semibold text-slate-900">Belum ada tugas</p>
          <p className="text-sm text-slate-500 mt-1">
            {canManage ? "Klik \"Buat Tugas\" untuk mulai." : "Belum ada tugas yang ditujukan ke Anda."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {items.map(p => {
              const st = STATUS.find(x => x.v === p.status) || STATUS[0];
              const pr = PRIORITY.find(x => x.v === p.priority) || PRIORITY[1];
              const overdue = p.deadline && p.deadline < hariIniISO() && p.status !== "Done";
              return (
                <motion.div key={p.id}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  data-testid={`project-${p.id}`}
                  className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex flex-col"
                  onClick={() => setDetailFor(p)}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-display font-semibold text-slate-900 truncate">{p.judul}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold whitespace-nowrap ${pr.tone}`}>{p.priority}</span>
                  </div>
                  {p.deskripsi && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{p.deskripsi}</p>}
                  <div className="flex items-center gap-1.5 flex-wrap mb-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-medium ${st.tone}`}>
                      <st.icon className={`w-3 h-3 ${p.status === "In-Progress" ? "animate-spin" : ""}`} />
                      {p.status}
                    </span>
                    {overdue && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 inline-flex items-center gap-1 font-medium">
                        <AlertCircle className="w-3 h-3" /> Terlambat
                      </span>
                    )}
                  </div>
                  <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1 text-slate-500 min-w-0">
                      <Users className="w-3 h-3 shrink-0" />
                      <span className="truncate">{p.assignees?.map(a => a.nama).join(", ") || "-"}</span>
                    </div>
                    {p.deadline && (
                      <div className="flex items-center gap-1 text-slate-500 shrink-0 ml-2">
                        <Calendar className="w-3 h-3" /> {p.deadline}
                      </div>
                    )}
                  </div>
                  {p.files?.length > 0 && (
                    <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-3">
                      <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {p.files.length} file</span>
                      {p.n_comments > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {p.n_comments}</span>}
                    </div>
                  )}
                  {(!p.files?.length && p.n_comments > 0) && (
                    <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> {p.n_comments} komentar
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* ---- New/Edit Modal ---- */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={projectGuard.requestClose}>
          <div ref={projectGuard.contentRef} className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">{editing ? "Edit Tugas" : "Buat Tugas Baru"}</h3>
              <button onClick={projectGuard.requestClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Judul Tugas *</span>
                <input value={form.judul} onChange={e => setForm(s => ({ ...s, judul: e.target.value }))}
                  data-testid="project-judul"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Deskripsi</span>
                <textarea rows={3} value={form.deskripsi} onChange={e => setForm(s => ({ ...s, deskripsi: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Prioritas</span>
                  <select value={form.priority} onChange={e => setForm(s => ({ ...s, priority: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {PRIORITY.map(p => <option key={p.v}>{p.v}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Status</span>
                  <select value={form.status} onChange={e => setForm(s => ({ ...s, status: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {STATUS.map(s => <option key={s.v}>{s.v}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Deadline</span>
                  <DatePicker value={form.deadline} onChange={v => setForm(s => ({ ...s, deadline: v }))}
                    className="mt-1" />
                </label>
              </div>

              <div>
                <span className="text-xs font-medium text-slate-600">Assign ke Karyawan *</span>
                <div className="mt-1 border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {emps.map(e => (
                    <label key={e.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-slate-50 rounded px-1">
                      <input type="checkbox" checked={form.employee_ids.includes(e.id)}
                        onChange={() => toggleAssignee(e.id)}
                        data-testid={`project-assign-${e.id}`} />
                      <span className="truncate">{e.nama} <span className="text-slate-400 text-[10px]">{e.branch}</span></span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-slate-600">Lampiran (max 10MB per file)</span>
                  <button onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                    <Upload className="w-3 h-3" /> Pilih file
                  </button>
                  <input ref={fileInputRef} type="file" multiple onChange={onPickFiles}
                    data-testid="project-file-input" className="hidden" />
                </div>
                {pendingFiles.length > 0 && (
                  <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 text-xs">
                    {pendingFiles.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 px-3 py-2">
                        <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="flex-1 truncate">{f.name}</span>
                        <span className="text-slate-400 text-[10px]">{idrShort(f.size).replace("Rp", "").trim()}B</span>
                        <button onClick={() => removePending(i)} className="p-1 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                          <X className="w-3 h-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={projectGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-save-project"
                  className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">
                  {editing ? "Perbarui" : "Simpan"}
                </button>
              </div>
              {projectGuard.confirming && (
                <UnsavedGuardBar onStay={projectGuard.cancelClose} onDiscard={projectGuard.confirmDiscard} testid="project-modal-guard-bar" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---- Detail Modal ---- */}
      {detailFor && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setDetailFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900 truncate">{detailFor.judul}</h3>
              <button onClick={() => setDetailFor(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              {detailFor.deskripsi && <p className="text-sm text-slate-700 whitespace-pre-wrap">{detailFor.deskripsi}</p>}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-slate-500">Prioritas</p><p className="font-medium">{detailFor.priority}</p></div>
                <div><p className="text-xs text-slate-500">Deadline</p><p className="font-medium">{detailFor.deadline || "-"}</p></div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-500 mb-1">Assignees</p>
                  <p className="font-medium">{detailFor.assignees?.map(a => a.nama).join(", ") || "-"}</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-slate-500 mb-2">Status</p>
                <div className="flex flex-wrap gap-1.5">
                  {STATUS.map(s => (
                    <button key={s.v} onClick={() => changeStatus(detailFor.id, s.v)}
                      data-testid={`project-status-${s.v}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        detailFor.status === s.v
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                      }`}>{s.v}</button>
                  ))}
                </div>
              </div>

              {detailFor.files?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Lampiran ({detailFor.files.length})</p>
                  <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 text-sm">
                    {detailFor.files.map(f => (
                      <li key={f.name} className="flex items-center gap-2 px-3 py-2">
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="truncate">{f.name}</p>
                          <p className="text-[10px] text-slate-400">{(f.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button onClick={() => downloadFile(detailFor.id, f.name)}
                          className="p-1.5 hover:bg-slate-100 rounded text-slate-500">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {canManage && (
                          <button onClick={() => deleteFile(detailFor.id, f.name)}
                            className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ---- Comments Thread ---- */}
              <CommentsThread
                detailFor={detailFor}
                userEmail={user?.email}
                canDeleteAny={canManage}
                onAdd={addComment}
                onDelete={deleteComment}
              />

              {canManage && (
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                  <button onClick={() => del(detailFor.id)}
                    className="h-10 px-4 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm inline-flex items-center gap-1">
                    <Trash2 className="w-4 h-4" /> Hapus
                  </button>
                  <button onClick={() => { setDetailFor(null); openEdit(detailFor); }}
                    className="h-10 px-5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
                    Edit Tugas
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CommentsThread({ detailFor, userEmail, canDeleteAny, onAdd, onDelete }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const ok = await onAdd(text);
    setSending(false);
    if (ok) { setText(""); inputRef.current?.focus(); }
  };

  const comments = detailFor.comments || [];
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="w-4 h-4 text-emerald-600" />
        <p className="text-xs text-slate-500 font-medium">Diskusi ({comments.length})</p>
      </div>

      {comments.length === 0 ? (
        <p className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg">
          Belum ada komentar. Mulai diskusi progres tugas di sini.
        </p>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="comments-list">
          {comments.map(c => {
            const isMine = c.author_email === userEmail;
            const canDel = isMine || canDeleteAny;
            const initial = (c.author_name || "?")[0]?.toUpperCase();
            return (
              <li key={c.id} className={`flex gap-2 ${isMine ? "flex-row-reverse" : ""}`}
                data-testid={`comment-${c.id}`}>
                <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-semibold shrink-0 ${
                  isMine ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
                }`}>{initial}</div>
                <div className={`flex-1 min-w-0 ${isMine ? "text-right" : ""}`}>
                  <div className={`inline-block max-w-full text-left px-3 py-2 rounded-2xl text-sm ${
                    isMine ? "bg-emerald-50 border border-emerald-100" : "bg-slate-50 border border-slate-100"
                  }`}>
                    <p className="text-[11px] font-semibold text-slate-700 mb-0.5">
                      {c.author_name}
                      <span className="ml-1.5 text-[10px] text-slate-400 font-normal uppercase">{c.author_role}</span>
                    </p>
                    <p className="whitespace-pre-wrap break-words text-slate-800">{c.text}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{formatTanggal(c.created_at, { denganJam: true })}</p>
                  </div>
                  {canDel && (
                    <button onClick={() => onDelete(c.id)}
                      data-testid={`btn-del-comment-${c.id}`}
                      className="text-[10px] text-slate-400 hover:text-rose-600 mt-0.5 ml-1">
                      hapus
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-end gap-2 mt-3">
        <textarea
          ref={inputRef}
          rows={2}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); submit(); }
          }}
          placeholder="Tulis komentar... (Ctrl+Enter untuk kirim)"
          data-testid="comment-input"
          className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-600/40"
        />
        <button
          onClick={submit}
          disabled={!text.trim() || sending}
          data-testid="btn-send-comment"
          className="h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-1.5"
        >
          <Send className="w-3.5 h-3.5" /> Kirim
        </button>
      </div>
    </div>
  );
}
